import React, { memo, useCallback, useEffect, useState } from 'react';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Flag, { FlagGroup } from '@atlaskit/flag';
import StatusError from '@atlaskit/icon/core/status-error';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { ContextualAnalyticsData, fireUIAnalytics, fireOperationalAnalytics, MODAL } from '@atlassian/analytics-bridge';
import { isFedRamp } from '@atlassian/atl-context';

import messages from './messages';
import type { Surface } from './type';
import { cleanupRovoWidgetSDKScript, loadRovoWidgetSDKScript } from './utils';

const InjectRovoServiceAgentWidgetScript = memo(function RovoServiceAgentWidget({
    siteId,
    widgetId,
    surface,
}: {
    siteId: string;
    widgetId: string;
    surface: Surface;
}) {
    di(loadRovoWidgetSDKScript, fireUIAnalytics, fireOperationalAnalytics, useAnalyticsEvents);

    const [error, setError] = useState<Error | null>(null);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { locale } = useIntl();

    const handleOnOpen = useCallback(() => {
        // https://data-portal.internal.atlassian.com/analytics/registry/88552
        fireUIAnalytics(createAnalyticsEvent({}), 'rovoServiceAgentWidget opened');
    }, [createAnalyticsEvent]);

    const handleOnClose = useCallback(() => {
        // https://data-portal.internal.atlassian.com/analytics/registry/88553
        fireUIAnalytics(createAnalyticsEvent({}), 'rovoServiceAgentWidget closed');
    }, [createAnalyticsEvent]);

    useEffect(() => {
        const loadScript = async () => {
            const startTime = performance.now();
            try {
                setError(null);
                await loadRovoWidgetSDKScript({
                    siteId,
                    widgetId,
                    surface,
                    locale,
                    onOpen: handleOnOpen,
                    onClose: handleOnClose,
                });

                // Measure widget load time (non-blocking, fire-and-forget)
                const loadDuration = Math.round(performance.now() - startTime);
                fireOperationalAnalytics(
                    createAnalyticsEvent({
                        attributes: {
                            surface,
                            duration: loadDuration,
                        },
                    }),
                    'rovoServiceAgentWidget scriptLoaded'
                );
            } catch (err) {
                // Measure failed load time (non-blocking)
                const loadDuration = Math.round(performance.now() - startTime);
                fireOperationalAnalytics(
                    createAnalyticsEvent({
                        attributes: {
                            surface,
                            duration: loadDuration,
                            error: err instanceof Error ? err.message : 'Unknown error',
                        },
                    }),
                    'rovoServiceAgentWidget scriptFailed'
                );
                setError(err instanceof Error ? err : new Error('Failed to load widget script'));
            }
        };

        // The error handler is inside loadScript, so we don't need to handle it here
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        loadScript();
    }, [widgetId, siteId, handleOnClose, handleOnOpen, surface, locale, createAnalyticsEvent]);

    useEffect(() => {
        return () => {
            cleanupRovoWidgetSDKScript();
        };
    }, []);

    if (error) {
        throw error;
    }

    // Return null without blocking UFO experience - widget loads asynchronously
    return null;
});

export const RovoServiceAgentWidgetInjector = ({
    siteId,
    widgetId,
    surface,
}: {
    siteId: string;
    widgetId: string;
    surface: Surface;
}) => {
    di(ErrorBoundary, UFOSegment, ContextualAnalyticsData, isFedRamp);

    const { formatMessage } = useIntl();
    const renderError = useCallback(() => {
        return (
            <FlagGroup>
                <Flag
                    id="rovo-widget-error-flag"
                    appearance="error"
                    icon={<StatusError label="" />}
                    title={formatMessage(messages.widgetErrorTitle)}
                    description={formatMessage(messages.widgetErrorDescription)}
                    testId="rovo-widget-error-flag"
                />
            </FlagGroup>
        );
    }, [formatMessage]);

    if (isFedRamp()) {
        return null;
    }

    return (
        <UFOSegment name="rovo-service-agent-widget">
            <ErrorBoundary packageName="rovo-service-agent" id="widget-injector" renderError={renderError}>
                <ContextualAnalyticsData
                    sourceName="rovoServiceAgent"
                    sourceType={MODAL}
                    attributes={{ siteId, widgetId, surface }}
                >
                    <InjectRovoServiceAgentWidgetScript siteId={siteId} widgetId={widgetId} surface={surface} />
                </ContextualAnalyticsData>
            </ErrorBoundary>
        </UFOSegment>
    );
};
